import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import APIUtil from 'util/APIUtil';

import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import type {
  HomepageV2EntriesQuery,
  HomepageV2EntriesQueryVariables,
  HomepageV2EntriesQuery_entries_EntryNode as EntryNode,
  HomepageV2EntriesQuery_stageEntries_StageEntryNode as StageEntryNode,
} from './__generated/HomepageV2EntriesQuery';
import {MyWorkCategory} from './MyWorkConstants';
import {reviewableRowProps} from './MyWorkRowHelpers';
import {MyWorkRowsFooterAction, MyWorkRowsList} from './MyWorkRowsList';
import {useMyWorkRows} from './useMyWorkRows';

type HomepageEntriesRowItem = EntryNode | StageEntryNode;

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

export const HOMEPAGE_ENTRIES_QUERY = gql`
  query HomepageV2EntriesQuery($entryIds: [StrictString!]!, $stageEntryIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NAVIGATION) {
    entries(ids: $entryIds) {
      ... on EntryNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
        humanId
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
    stageEntries(ids: $stageEntryIds) {
      ... on StageEntryNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
        humanId
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

/** Fetches and lists notebook entries and stage entries for the selected review-status filter. */
export default function EntriesRows({
  footerAction,
  hasCategoryWork,
  selectedEntryIds,
}: {
  footerAction?: MyWorkRowsFooterAction;
  hasCategoryWork: boolean;
  selectedEntryIds: Array<string> | null;
}): JSX.Element {
  const [entryIds, stageEntryIds] = _.partition(selectedEntryIds ?? [], (id) => APIUtil.isEntry(id));

  const rowsResult = useMyWorkRows<
    HomepageV2EntriesQuery,
    HomepageV2EntriesQueryVariables,
    HomepageEntriesRowItem
  >({
    query: HOMEPAGE_ENTRIES_QUERY,
    variables: {entryIds, stageEntryIds},
    skip: selectedEntryIds == null,
    getItems: (data) => [...data.entries, ...data.stageEntries],
    toRowProps: (item) => reviewableRowProps(item),
    errorMessage: 'Error loading entries',
    testComponent: TEST_COMPONENT,
  });

  if (rowsResult.type !== QueryResultType.SUCCESS) {
    return rowsResult.element;
  }

  return (
    <MyWorkRowsList
      rows={rowsResult.rows}
      category={MyWorkCategory.ENTRIES}
      hasCategoryWork={hasCategoryWork}
      footerAction={footerAction}
      emptyMessageDataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'messageRow',
        key: 'emptyEntries',
      }}
    />
  );
}
